/**
 * StaffForm — modal-content form for inviting a new staff member.
 *
 * Issue #31 AC2: "Given I add a new staff member, when I submit, then
 * a StaffUser + TenantUser row is created with an invite email sent."
 *
 * The form takes email + first/last name + role. On submit, the server's
 * `POST /api/v2/admin/staff/invite` creates the TenantUser
 * (status=invited), dispatches the magic-link invite email, and the
 * existing accept-flow bridges TenantUser → StaffUser the first time
 * the invitee sets their password.
 *
 * #221 Wave 1: converted from inert Tailwind utilities to the ferro-*
 * form vocabulary — First/Last sit side-by-side ≥540px and stack at
 * 375, inputs get real borders + focus rings, Cancel/Send render as an
 * equally-styled ≥44px button pair, and errors land in a styled alert.
 */
import { useState } from 'react';

export interface StaffFormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export interface StaffFormProps {
  /** Role options shown in the dropdown — match the server's role allow-list. */
  roleOptions: ReadonlyArray<{ value: string; label: string }>;
  initialRole?: string;
  isSubmitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (values: StaffFormValues) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StaffForm({
  roleOptions,
  initialRole,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: StaffFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Default to the first offered (canonical) role rather than a hardcoded
  // 'staff' — which is no longer a valid role; the server now rejects it.
  const [role, setRole] = useState(initialRole ?? roleOptions[0]?.value ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setLocalError(null);
    const cleanEmail = email.trim().toLowerCase();
    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();
    if (!cleanEmail || !cleanFirst || !cleanLast) {
      setLocalError('All fields are required');
      return;
    }
    if (!EMAIL_REGEX.test(cleanEmail)) {
      setLocalError('Enter a valid email address');
      return;
    }
    onSubmit({ email: cleanEmail, firstName: cleanFirst, lastName: cleanLast, role });
  };

  const visibleError = error ?? localError;

  return (
    <form
      className="ferro-form"
      onSubmit={handleSubmit}
      aria-label="Invite staff member"
    >
      <div className="ferro-form__row">
        <div className="ferro-field">
          <label htmlFor="staff-first-name" className="ferro-field__label">
            First Name
          </label>
          <input
            id="staff-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="ferro-field__input"
          />
        </div>
        <div className="ferro-field">
          <label htmlFor="staff-last-name" className="ferro-field__label">
            Last Name
          </label>
          <input
            id="staff-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="ferro-field__input"
          />
        </div>
      </div>

      <div className="ferro-field">
        <label htmlFor="staff-email" className="ferro-field__label">
          Email
        </label>
        <input
          id="staff-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="ferro-field__input"
        />
        <p className="ferro-field__helper">
          We'll email a magic-link invite. They set their own password.
        </p>
      </div>

      <div className="ferro-field">
        <label htmlFor="staff-role" className="ferro-field__label">
          Role
        </label>
        <select
          id="staff-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="ferro-field__input"
        >
          {roleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {visibleError ? (
        <div className="ferro-alert ferro-alert--error" role="alert">
          {visibleError}
        </div>
      ) : null}

      <div className="ferro-form__actions ferro-form__actions--fill">
        <button
          type="button"
          onClick={onCancel}
          className="ferro-btn ferro-btn--outline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="ferro-btn ferro-btn--primary"
        >
          {isSubmitting ? 'Sending invite...' : 'Send invite'}
        </button>
      </div>
    </form>
  );
}
